import { useEffect, useState } from "react";
import { ChartLineUp, Plus, PencilSimple, Trash, Target } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { STATI_OBIETTIVO } from "@/lib/constants";
import { toast } from "sonner";

const emptyObj = () => ({ descrizione: "", indicatore: "", target: "", scadenza: "", stato: "aperto" });
const emptyForm = () => ({ tenant_id: "", data: "", inputs: [], outputs: [], note: "", obiettivi: [] });

const objBadge = (s) => {
  const map = {
    aperto: "bg-blue-100 text-blue-800 border-blue-200",
    raggiunto: "bg-emerald-100 text-emerald-700 border-emerald-200",
    "non raggiunto": "bg-rose-100 text-rose-700 border-rose-200",
  };
  return <Badge className={`${map[s] || ""} capitalize`}>{s}</Badge>;
};

export default function Reviews() {
  const { tenantId, organizations, currentOrg } = useTenant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [inputsStr, setInputsStr] = useState("");
  const [outputsStr, setOutputsStr] = useState("");
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    setLoading(true);
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/management-reviews${p}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [tenantId]);

  const openNew = () => {
    setEditingId(null); setForm({ ...emptyForm(), tenant_id: tenantId || "" });
    setInputsStr(""); setOutputsStr(""); setOpen(true);
  };
  const openEdit = (r) => {
    setEditingId(r.id); setForm({ ...emptyForm(), ...r, obiettivi: (r.obiettivi || []).map((o) => ({ ...o })) });
    setInputsStr((r.inputs || []).join("\n")); setOutputsStr((r.outputs || []).join("\n")); setOpen(true);
  };

  const addObj = () => setForm((f) => ({ ...f, obiettivi: [...f.obiettivi, emptyObj()] }));
  const removeObj = (i) => setForm((f) => ({ ...f, obiettivi: f.obiettivi.filter((_, idx) => idx !== i) }));
  const editObj = (i, k, v) => setForm((f) => ({ ...f, obiettivi: f.obiettivi.map((o, idx) => idx === i ? { ...o, [k]: v } : o) }));

  const save = async () => {
    if (!form.tenant_id) { toast.error("Seleziona un cliente"); return; }
    if (!form.data) { toast.error("Inserisci la data del riesame"); return; }
    const payload = {
      ...form,
      inputs: inputsStr.split("\n").map((s) => s.trim()).filter(Boolean),
      outputs: outputsStr.split("\n").map((s) => s.trim()).filter(Boolean),
      obiettivi: form.obiettivi.filter((o) => o.descrizione.trim()),
    };
    delete payload.id; delete payload.created_at;
    try {
      if (editingId) { await api.put(`/management-reviews/${editingId}`, payload); toast.success("Riesame aggiornato"); }
      else { await api.post("/management-reviews", payload); toast.success("Riesame creato"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo riesame?")) return;
    try { await api.delete(`/management-reviews/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const orgName = (tid) => organizations.find((o) => o.id === tid)?.nome || "";

  return (
    <div className="space-y-6">
      <PageHero label="UNI EN ISO 9001:2015 §9.3" title="Riesame della direzione"
        subtitle={tenantId ? currentOrg?.nome : "Riesami periodici con input, output e obiettivi collegati"}>
        <Button onClick={openNew} data-testid="new-review-btn"
          className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
          <Plus className="h-4 w-4 mr-2" /> Nuovo riesame
        </Button>
      </PageHero>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <ChartLineUp size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessun riesame registrato{tenantId ? "" : ". Seleziona un cliente per aggiungerne"}.</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="reviews-list">
          {items.map((r) => (
            <Card key={r.id} data-testid={`review-${r.id}`} className="p-5 border-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-slate-900">Riesame del {r.data || "—"}</h3>
                  {!tenantId && <div className="text-xs text-slate-500">{orgName(r.tenant_id)}</div>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-review-${r.id}`} onClick={() => openEdit(r)}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" data-testid={`delete-review-${r.id}`} onClick={() => remove(r.id)}><Trash size={16} /></Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <div className="label-caps mb-1">Input</div>
                  {(r.inputs || []).length ? <ul className="list-disc pl-5 text-sm text-slate-600 space-y-0.5">{r.inputs.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="text-xs text-slate-400">—</p>}
                </div>
                <div>
                  <div className="label-caps mb-1">Output</div>
                  {(r.outputs || []).length ? <ul className="list-disc pl-5 text-sm text-slate-600 space-y-0.5">{r.outputs.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="text-xs text-slate-400">—</p>}
                </div>
              </div>
              {r.note && <p className="text-sm text-slate-500 mt-3 italic">{r.note}</p>}
              {(r.obiettivi || []).length > 0 && (
                <div className="mt-4">
                  <div className="label-caps mb-2 flex items-center gap-1.5"><Target size={14} /> Obiettivi</div>
                  <div className="space-y-2">
                    {r.obiettivi.map((o, i) => (
                      <div key={i} className="flex items-start justify-between gap-2 rounded-md bg-slate-50 p-2.5">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800">{o.descrizione}</div>
                          <div className="text-xs text-slate-500">{[o.indicatore, o.target && `target ${o.target}`, o.scadenza && `entro ${o.scadenza}`].filter(Boolean).join(" · ")}</div>
                        </div>
                        {objBadge(o.stato)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Modifica riesame" : "Nuovo riesame della direzione"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              {!editingId && (
                <div>
                  <Label>Cliente</Label>
                  <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="review-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                    <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Data riesame</Label><Input type="date" value={form.data || ""} data-testid="review-data" onChange={(e) => setForm({ ...form, data: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Input (uno per riga)</Label><Textarea value={inputsStr} data-testid="review-inputs" onChange={(e) => setInputsStr(e.target.value)} className="mt-1.5" rows={4} placeholder="Esiti audit&#10;Feedback clienti&#10;Stato azioni precedenti" /></div>
              <div><Label>Output (uno per riga)</Label><Textarea value={outputsStr} data-testid="review-outputs" onChange={(e) => setOutputsStr(e.target.value)} className="mt-1.5" rows={4} placeholder="Azioni di miglioramento&#10;Risorse necessarie&#10;Modifiche al sistema" /></div>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" rows={2} /></div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Obiettivi collegati</Label>
                <Button size="sm" variant="outline" onClick={addObj} data-testid="add-objective-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Aggiungi obiettivo</Button>
              </div>
              <div className="mt-2 space-y-3">
                {form.obiettivi.map((o, i) => (
                  <div key={i} className="rounded-md border border-slate-200 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={o.descrizione} onChange={(e) => editObj(i, "descrizione", e.target.value)} placeholder="Descrizione obiettivo" className="h-8 flex-1" data-testid={`objective-desc-${i}`} />
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeObj(i)}><Trash size={14} /></Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Input value={o.indicatore} onChange={(e) => editObj(i, "indicatore", e.target.value)} placeholder="Indicatore" className="h-8" />
                      <Input value={o.target} onChange={(e) => editObj(i, "target", e.target.value)} placeholder="Target" className="h-8" />
                      <Input type="date" value={o.scadenza || ""} onChange={(e) => editObj(i, "scadenza", e.target.value)} className="h-8" />
                      <Select value={o.stato} onValueChange={(v) => editObj(i, "stato", v)}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATI_OBIETTIVO.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-review-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
